/**
 * NotificationBell — admin-only topbar bell with unread count + dropdown.
 *
 * Polls /notifications/unread-count every 30s. Opens a dropdown listing
 * the latest 50 notifications; clicking one marks it read and navigates
 * to its link. Sellers see no bell (their role doesn't receive in-app
 * notifications in the current product).
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Check, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { api } from "@/App";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diffMs = Date.now() - t;
    const m = Math.round(diffMs / 60000);
    if (m < 1) return "hace un momento";
    if (m < 60) return `hace ${m} min`;
    const h = Math.round(m / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.round(h / 24);
    return `hace ${d} d`;
  } catch {
    return "";
  }
}

export default function NotificationBell({ role }) {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data?.unread || 0);
    } catch {
      /* silent */
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 50 } });
      setItems(data || []);
    } catch {
      toast.error("No se pudieron cargar las notificaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (role === "seller") return undefined;
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [role, fetchUnread]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const markRead = async (n) => {
    try {
      await api.post(`/notifications/${n.id}/read`);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    } catch {
      /* silent */
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
      toast.success("Todas marcadas como leídas");
    } catch {
      toast.error("Error al marcar como leídas");
    }
  };

  const openNotif = async (n) => {
    if (!n.read) await markRead(n);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const remove = async (n) => {
    try {
      await api.delete(`/notifications/${n.id}`);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
      if (!n.read) setUnread((u) => Math.max(0, u - 1));
    } catch {
      /* silent */
    }
  };

  if (role === "seller") return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-sm"
          aria-label="Notificaciones"
          data-testid="notifications-bell"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center text-[10px] font-black rounded-full bg-red-600 text-white border-2 border-white"
              data-testid="notifications-unread-badge"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 sm:w-96 rounded-sm p-0 max-h-[70vh] overflow-hidden flex flex-col"
        data-testid="notifications-menu"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-600">
            Notificaciones
          </p>
          <button
            type="button"
            onClick={markAll}
            disabled={unread === 0}
            className="text-[11px] font-semibold text-[#002FA7] hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="notifications-mark-all"
          >
            Marcar todas leídas
          </button>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {loading && (
            <p className="text-xs text-slate-500 italic py-6 text-center">Cargando…</p>
          )}
          {!loading && items.length === 0 && (
            <p className="text-xs text-slate-500 italic py-8 text-center">
              Sin notificaciones aún.
            </p>
          )}
          {items.map((n) => (
            <div
              key={n.id}
              className={`flex items-start gap-2 px-3 py-2.5 hover:bg-slate-50 ${
                n.read ? "bg-white" : "bg-blue-50/40"
              }`}
              data-testid={`notification-item-${n.id}`}
            >
              <button
                type="button"
                onClick={() => openNotif(n)}
                className="flex-1 min-w-0 text-left"
                data-testid={`notification-open-${n.id}`}
              >
                <div className="flex items-center gap-1.5">
                  {!n.read && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#002FA7]" aria-hidden />
                  )}
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {n.title}
                  </p>
                </div>
                <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">
                  {timeAgo(n.created_at)}
                </p>
              </button>
              <div className="flex flex-col gap-1">
                {!n.read && (
                  <button
                    type="button"
                    onClick={() => markRead(n)}
                    className="p-1 text-slate-400 hover:text-emerald-600"
                    title="Marcar como leída"
                    data-testid={`notification-read-${n.id}`}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(n)}
                  className="p-1 text-slate-400 hover:text-red-600"
                  title="Eliminar"
                  data-testid={`notification-delete-${n.id}`}
                >
                  <XIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="px-3 py-2 border-t border-slate-200 bg-slate-50">
          <Link
            to="/company"
            onClick={() => setOpen(false)}
            className="text-xs font-semibold text-[#002FA7] hover:underline"
            data-testid="notifications-see-all"
          >
            Ver toda la actividad →
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
